// src/components/EquipmentManager/AssignmentsTab.jsx

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaUsers, FaCheckCircle, FaClock, FaExclamationTriangle, FaStickyNote } from 'react-icons/fa';
import styles from './AssignmentsTab.module.css';

const AssignmentsTab = ({ equipment, programId }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch assignments for this specific equipment
  useEffect(() => {
    const fetchAssignments = async () => {
      if (!programId || !equipment._id) return;
      
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/equipment-assignments/program/${programId}`
        );
        
        if (response.ok) {
          const data = await response.json();
          const assignmentsArray = Array.isArray(data) ? data : (data.assignments || []);
          
          // Filter assignments for this specific equipment
          const equipmentAssignments = assignmentsArray.filter(assignment => {
            const assignmentEquipmentId = assignment.equipmentId?._id || assignment.equipmentId;
            return String(assignmentEquipmentId) === String(equipment._id);
          });
          
          setAssignments(equipmentAssignments);
        }
      } catch (error) {
        console.error('Error fetching assignments:', error);
        setAssignments([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAssignments();
  }, [equipment._id, programId]);
  
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'returned':
        return <FaCheckCircle className={styles.statusIconReturned} />;
      case 'overdue':
        return <FaExclamationTriangle className={styles.statusIconOverdue} />;
      default:
        return <FaClock className={styles.statusIconActive} />;
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
  
  if (loading) {
    return (
      <div className={styles.emptyState}>
        <p>Loading assignments...</p>
      </div>
    );
  }
  
  if (assignments.length === 0) {
    return (
      <div className={styles.emptyState}>
        <FaUsers className={styles.emptyIcon} />
        <p>No assignments for this equipment yet</p>
      </div>
    );
  }
  
  return (
    <div className={styles.assignmentsTab}>
      <div className={styles.assignmentsList}>
        {assignments.map((assignment) => (
          <div key={assignment._id} className={styles.assignmentCard}>
            <div className={styles.cardHeader}>
              <div className={styles.playerInfo}>
                <div className={styles.playerAvatar}>
                  {(assignment.playerId?.firstName || 'U').charAt(0).toUpperCase()}
                </div>
                <div className={styles.playerDetails}>
                  <h4 className={styles.playerName}>
                    {assignment.playerId?.firstName || 'Unknown'} {assignment.playerId?.lastName || ''}
                  </h4>
                  <span className={styles.assignedDate}>
                    Assigned: {formatDate(assignment.createdAt || assignment.assignedDate)}
                  </span>
                </div>
              </div>
              
              <div className={styles.statusBadge}>
                {getStatusIcon(assignment.status)}
                <span className={styles.statusText}>{assignment.status || 'Active'}</span>
              </div>
            </div>
            
            <div className={styles.cardBody}>
              <div className={styles.assignmentDetails}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Due Date:</span>
                  <span className={styles.detailValue}>{formatDate(assignment.dueDate)}</span>
                </div>
                
                {assignment.returnedAt && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Returned:</span>
                    <span className={styles.detailValue}>{formatDate(assignment.returnedAt)}</span>
                  </div>
                )}
                
                {assignment.quantity && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Quantity:</span>
                    <span className={styles.detailValue}>{assignment.quantity}</span>
                  </div>
                )}
                
                {assignment.condition && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Condition:</span>
                    <span className={styles.detailValue}>{assignment.condition}</span>
                  </div>
                )}
              </div>
              
              {assignment.notes && (
                <div className={styles.notesSection}>
                  <FaStickyNote className={styles.notesIcon} />
                  <p className={styles.notesText}>{assignment.notes}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

AssignmentsTab.propTypes = {
  equipment: PropTypes.object.isRequired,
  programId: PropTypes.string.isRequired
};

export default AssignmentsTab;

